import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import { Download as DownloadIcon } from "@mui/icons-material";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import s3Utils from '../../../utils/s3Utils';
import { decryptAppId, decryptText } from '../../../utils/encryption';
import { getAuditTrailLogs } from '../../../services/apiCalibration';


const AuditTrail = () => {
  const location = useLocation();
  const [labDetailsState, setlabDetailsState] = useState([]);
  const [disciplineState, setDiscipline] = useState([]);
  const [locationDetailsState, setLocationDetails] = useState([]);
  const [scopeState, setScope] = useState([]);
  const [organisationDetailsState, setOrganisationDetails] = useState([]);
  const [equipmentDetailsState, setEquipmentDetails] = useState([]);
  const [referenceMaterialsState, setReferenceMaterials] = useState([]);
  const [observationsState, setObservations] = useState([]);
  const [nonAnalyticalInstrumentsState, setNonAnalyticalInstruments] = useState([]);
  const [form153PhantomsState, setForm153Phantoms] = useState([]);
  const [radioDetailsState, setRadioDetails] = useState([]);
  const [auditTrailLogsState, setAuditTrailLogs] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [isObservationVisible, setIsObservationVisible] = useState(false);
  const [loadingAuditTrail, setLoadingAuditTrail] = useState(false);
  const [selectedSection, setSelectedSection] = useState('all');
  const [appIdState, setAppIdState] = useState(null);
  const [orgIdState, setOrgIdState] = useState(null);

  useEffect(() => {
    const encryptedAppId = getQueryParam("appId");
    const encryptedOrgId = getQueryParam("orgId");
    const encryptedObservation = getQueryParam("observation");
    
    console.log('🔍 PreviewReport - Encrypted parameters received:', {
      encryptedAppId,
      encryptedOrgId,
      encryptedObservation
    });

    if (encryptedAppId && encryptedOrgId && encryptedObservation) {
      try {
        // Decrypt the parameters
        const decryptedAppId = decryptAppId(encryptedAppId);
        const decryptedOrgId = decryptText(encryptedOrgId);
        const decryptedObservation = decryptText(encryptedObservation);

        console.log('🔓 PreviewReport - Decrypted parameters:', {
          appId: decryptedAppId,
          orgId: decryptedOrgId,
          observation: decryptedObservation
        });

        // Use decrypted parameters
        if (decryptedAppId && decryptedOrgId) {
          setAppIdState(decryptedAppId);
          setOrgIdState(decryptedOrgId);
          fetchForm153Data(decryptedAppId, decryptedOrgId);
        }
        setIsObservationVisible(decryptedObservation === "true");

        console.log('✅ PreviewReport - Parameters successfully decrypted and used');
      } catch (error) {
        console.error('❌ Error decrypting preview report parameters:', error);
        // Fallback to original behavior if decryption fails
        const appId = encryptedAppId;
        const orgId = encryptedOrgId;
        const observation = encryptedObservation;
        if (appId && orgId) {
          setAppIdState(appId);
          setOrgIdState(orgId);
          fetchForm153Data(appId, orgId);
        }
        setIsObservationVisible(observation === "true");
      }
    }
  }, [location.search]);

  // Fetch audit trail logs after scope and equipment data is loaded
  useEffect(() => {
    if (appIdState && orgIdState && scopeState.length > 0) {
      fetchAuditTrailLogs(appIdState, orgIdState);
    }
  }, [scopeState, equipmentDetailsState, referenceMaterialsState]);

  const getQueryParam = (param) => {
    const params = new URLSearchParams(location.search);
    return params.get(param);
  };

  const fetchForm153Data = async (appId, orgId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/preview-report/${appId}/${orgId}`
      );

      if (response.data.success) {
        const {
          labDetails,
          discipline,
          locationDetails,
          scope,
          organisationDetails,
          equipmentDetails,
          referenceMaterials,
          observations,
          nonAnalyticalInstruments,
          form153Phantoms,
          form153KitsRadioisotopes
        } = response.data.data;

        setlabDetailsState(labDetails);
        setDiscipline(discipline);
        setLocationDetails(locationDetails);
        setScope(scope);
        setOrganisationDetails(organisationDetails);
        setEquipmentDetails(equipmentDetails);
        setReferenceMaterials(referenceMaterials);
        setObservations(observations);
        setNonAnalyticalInstruments(nonAnalyticalInstruments);
        setForm153Phantoms(form153Phantoms);
        setRadioDetails(form153KitsRadioisotopes);
      } else {
        console.error("API Error:", response.data.message);
      }
    } catch (error) {
      console.error("Request Failed:", error.message);
    }
  };

  const fetchAuditTrailLogs = async (appId, orgId) => {
    try {
      setLoadingAuditTrail(true);
      const response = await getAuditTrailLogs(orgId, appId);

      if (response.data.success) {
        const processedData = processAuditTrailData(
          response.data.data, 
          scopeState, 
          equipmentDetailsState, 
          referenceMaterialsState
        );
        setAuditTrailLogs(processedData);
        console.log('✅ Audit trail logs fetched successfully:', response.data.data.length, 'records');
      } else {
        console.error("Audit Trail API Error:", response.data.message);
        setAuditTrailLogs([]);
      }
    } catch (error) {
      console.error("Audit Trail Request Failed:", error.message);
      setAuditTrailLogs([]);
    } finally {
      setLoadingAuditTrail(false);
    }
  };

  // Process audit trail data to group by section and source ID with multiple observations per row
  const processAuditTrailData = (data, scopeData = [], equipmentData = [], referenceMaterialsData = []) => {
    if (!data || data.length === 0) return [];

    // Create lookup maps for scope and equipment data using Id field
    const scopeMap = {};
    scopeData.forEach(scope => {
      if (scope.Id) {
        scopeMap[scope.Id] = {
          scopeName: scope.TypeOfTest ? `${scope.GroupTested || ''} - ${scope.TypeOfTest}`.trim() : '',
          equipmentId: scope.EquipmentID,
          equipmentName: scope.EquipmentName || '',
          ptName: scope.Pt_id || '',
          refId: scope.Ref_Id || ''
        };
      }
    });

    const equipmentMap = {};
    equipmentData.forEach(equipment => {
      if (equipment.Id) {
        equipmentMap[equipment.Id] = equipment.NameOfEquipment || '';
      }
    });

    // Create reference materials map
    const referenceMaterialsMap = {};
    referenceMaterialsData.forEach(ref => {
      if (ref.Id) {
        referenceMaterialsMap[ref.Id] = ref.Name || '';
      }
    });

    // Create staff/personnel map
    const staffMap = {};
    organisationDetailsState.forEach(staff => {
      if (staff.Id) {
        staffMap[staff.Id] = staff.Name || '';
      }
    });

    // Create non-analytical instruments map
    const nonAnalyticalInstrumentsMap = {};
    nonAnalyticalInstrumentsState.forEach(instrument => {
      if (instrument.Id) {
        nonAnalyticalInstrumentsMap[instrument.Id] = instrument.NameOfEquipment || instrument.Name || '';
      }
    });

    console.log('📊 Scope Map created with', Object.keys(scopeMap).length, 'entries');
    console.log('🔧 Equipment Map created with', Object.keys(equipmentMap).length, 'entries');
    console.log('📋 Reference Materials Map created with', Object.keys(referenceMaterialsMap).length, 'entries');
    console.log('👤 Staff Map created with', Object.keys(staffMap).length, 'entries');
    console.log('🔬 Non-Analytical Instruments Map created with', Object.keys(nonAnalyticalInstrumentsMap).length, 'entries');

    // Sort by insert date (oldest first for proper chronological order)
    const sortedData = [...data].sort((a, b) => {
      const dateA = new Date(a.Insertdate || a.updatedate);
      const dateB = new Date(b.Insertdate || b.updatedate);
      return dateA - dateB; // Oldest first for chronological numbering
    });

    // Group by section and source ID combination
    const groupedData = {};
    let observationCounter = 0;

    sortedData.forEach((record) => {
      const sectionKey = record.SectionName || 'General';
      const sourceKey = record.sourceid || '0';
      const combinationKey = `${sectionKey}_${sourceKey}`;
      
      if (!groupedData[combinationKey]) {
        observationCounter++;
        
        // Look up scope/equipment/reference materials/staff/non-analytical instruments details using sourceid (which matches Id)
        const scopeInfo = scopeMap[sourceKey] || {};
        let equipmentName = equipmentMap[sourceKey] || '';
        let referenceMaterialName = referenceMaterialsMap[sourceKey] || '';
        let staffName = staffMap[sourceKey] || '';
        let nonAnalyticalInstrumentName = nonAnalyticalInstrumentsMap[sourceKey] || '';
        
        // Determine which detail to show based on section name
        let displayName = '';
        const sectionLower = sectionKey.toLowerCase();
        
        if (sectionLower.includes('scope')) {
          // Show scope name for Scope section
          displayName = scopeInfo.scopeName || '';
          console.log(`🔍 Scope Section - Source ID ${sourceKey}: Scope="${displayName}"`);
        } else if (sectionLower.includes('equipment') || sectionLower.includes('instrument')) {
          // Show equipment name for Equipment section
          displayName = equipmentName || '';
          console.log(`🔧 Equipment Section - Source ID ${sourceKey}: Equipment="${displayName}"`);
        } else if (sectionLower.includes('non-analytical') || sectionLower.includes('non analytical')) {
          // Show non-analytical instrument name for Non-Analytical Instruments section
          displayName = nonAnalyticalInstrumentName || '';
          console.log(`🔬 Non-Analytical Instruments Section - Source ID ${sourceKey}: Instrument="${displayName}"`);
        } else if (sectionLower.includes('pt') || sectionLower.includes('proficiency')) {
          // Show PT name for PT section
          displayName = scopeInfo.ptName || '';
          console.log(`📊 PT Section - Source ID ${sourceKey}: PT="${displayName}"`);
        } else if (sectionLower.includes('reference material')) {
          // Show reference material name for Reference Material section
          displayName = referenceMaterialName || '';
          console.log(`📋 Reference Material Section - Source ID ${sourceKey}: Material="${displayName}"`);
        } else if (sectionLower.includes('staff') || sectionLower.includes('personnel') || sectionLower.includes('organisation')) {
          // Show staff name for Staff section
          displayName = staffName || '';
          console.log(`👤 Staff Section - Source ID ${sourceKey}: Staff="${displayName}"`);
        }
        
        groupedData[combinationKey] = {
          id: combinationKey,
          observationNumber: observationCounter,
          observationLabel: `${sectionKey} - Observation ${observationCounter}`,
          sectionName: sectionKey,
          sourceId: sourceKey,
          records: [],
          latestDate: null,
          latestStatus: null,
          personnel: record.personnel || '',
          sectionDetails: record.SectionDetails || '',
          // Display the appropriate name based on section type
          scopeName: displayName,
          equipmentName: '',
          ptName: '',
          referenceMaterialName: '',
          allObservations: [],
          allStatuses: [],
          allDates: []
        };
      }

      // Add this record to the group
      groupedData[combinationKey].records.push(record);
      
      // Track all observations, statuses, and dates
      if (record.Observation && record.Observation.trim() !== '') {
        groupedData[combinationKey].allObservations.push(record.Observation);
      }
      
      if (record.Status && record.Status.trim() !== '') {
        groupedData[combinationKey].allStatuses.push(record.Status);
      }
      
      const recordDate = new Date(record.Insertdate || record.updatedate);
      groupedData[combinationKey].allDates.push(recordDate);
      
      // Keep track of latest date and status
      if (!groupedData[combinationKey].latestDate || recordDate > groupedData[combinationKey].latestDate) {
        groupedData[combinationKey].latestDate = recordDate;
        groupedData[combinationKey].latestStatus = record.Status || '';
      }
    });

    // Define section order
    const sectionOrder = [
      'Laboratory Details',
      'Legal Entity Type',
      'Type of Functioning',
      'Contact Person from laboratory for NABL',
      'Parent Organization',
      'Adverse Action History',
      'SEZ Region',
      'NABL Accreditation Consultancy',
      'Category of Medical Testing Facility',
      'Collection of Sample From',
      'Internal Audit and Management Review',
      'Organization Details',
      'Equipment',
      'Scope',
      'Non-Analytical Instruments',
      'Proficiency Testing Document',
      'Staff',
      'Reference Material',
      'Declaration Section'
    ];

    // Create a map for section priority
    const sectionPriorityMap = {};
    sectionOrder.forEach((section, index) => {
      sectionPriorityMap[section.toLowerCase()] = index;
    });

    // Function to get section priority
    const getSectionPriority = (sectionName) => {
      const sectionLower = sectionName.toLowerCase();
      // Try exact match first
      if (sectionPriorityMap[sectionLower] !== undefined) {
        return sectionPriorityMap[sectionLower];
      }
      // Try partial match
      for (const [key, value] of Object.entries(sectionPriorityMap)) {
        if (sectionLower.includes(key) || key.includes(sectionLower)) {
          return value;
        }
      }
      // Default priority for unknown sections (put at end)
      return 9999;
    };

    // Convert grouped data to array and sort by section order first, then observation number
    const processedData = Object.values(groupedData).sort((a, b) => {
      const priorityA = getSectionPriority(a.sectionName);
      const priorityB = getSectionPriority(b.sectionName);
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // If same section, sort by observation number
      return a.observationNumber - b.observationNumber;
    });

    return processedData;
  };

  // Get formatted observation display text
  const getObservationDisplayText = (record) => {
    if (!record.Observation || record.Observation.trim() === '') {
      return '-';
    }

    // For numbered observations, just show the observation text 
    // (the label is shown separately in the UI)
    return record.Observation;
  };

  // Get section summary for audit trail
  const getAuditTrailSummary = () => {
    if (!auditTrailLogsState || auditTrailLogsState.length === 0) return null;

    const sectionCounts = {};
    let totalObservations = 0;

    auditTrailLogsState.forEach((record) => {
      const section = record.SectionName || 'General';
      if (!sectionCounts[section]) {
        sectionCounts[section] = { total: 0, observations: 0 };
      }
      sectionCounts[section].total++;
      
      if (record.Observation && record.Observation.trim() !== '') {
        sectionCounts[section].observations++;
        totalObservations++;
      }
    });

    return {
      totalRecords: auditTrailLogsState.length,
      totalObservations,
      sectionCounts
    };
  };

  // Get unique sections from audit trail data
  const getAvailableSections = () => {
    if (!auditTrailLogsState || auditTrailLogsState.length === 0) return [];
    
    const sections = [...new Set(auditTrailLogsState.map(record => record.SectionName || 'General'))];
    return sections.sort();
  };

  // Get filtered audit trail data based on selected section
  const getFilteredAuditTrailData = () => {
    if (!auditTrailLogsState || auditTrailLogsState.length === 0) return [];
    
    if (selectedSection === 'all') {
      return auditTrailLogsState;
    }
    
    return auditTrailLogsState.filter(record => 
      (record.SectionName || 'General') === selectedSection
    );
  };

  const getObservationBySection = (sectionName, sourceId) => {
    if (sourceId) {
      return observationsState.find(
        (item) =>
          item.SectionName &&
          item.SectionName.toLowerCase() === sectionName.toLowerCase() &&
          item.sourceid === sourceId
      );
    } else {
      return observationsState.find(
        (item) =>
          item.SectionName &&
          item.SectionName.toLowerCase() === sectionName.toLowerCase()
      );
    }
  };

  const getObservationBySubSection = (sectionName, subSectionName = "") => {
    return observationsState.find(
      (item) =>
        item.SectionName?.toLowerCase() === sectionName.toLowerCase() &&
        (subSectionName
          ? item.SubsectionName?.toLowerCase() === subSectionName.toLowerCase()
          : true)
    );
  };

  // Get observation text from the Observation field
  const getObservationText = (observationData) => {
    if (!observationData) return "";
    return observationData.Observation || "";
  };


  // Render observation cell content - displays the Observation text and status
  const renderObservationCell = (observationData) => {
    if (!observationData) return "No observation";
    
    const observation = observationData.Observation;
    const status = observationData.InformationStatus;
    const informationProvided = observationData.InformationProvided;
    
    // Check if observation text is "Document adequacy check completed" and status is Adequate
    const isAdequate = observation && 
                       observation.trim().toLowerCase() === "document adequacy check completed" &&
                       status && status.toLowerCase() === "adequate";
    
    // If status is Adequate, show green "Adequate"
    if (isAdequate) {
      return (
        <div style={{ textAlign: "left" }}>
          <span style={{ color: "green", fontWeight: "bold" }}>Adequate</span>
        </div>
      );
    }
    
    // If status is Inadequate, show InformationProvided in red
    if (status && status.toLowerCase() === "inadequate") {
      return (
        <div style={{ textAlign: "left" }}>
          <span style={{ color: "red", fontWeight: "bold" }}>
            {informationProvided || observation || "No information provided"}
          </span>
        </div>
      );
    }
    
    // Default: show observation in black
    return (
      <div style={{ textAlign: "left" }}>
        <span style={{ color: "black" }}>
          {observation || "No observation"}
        </span>
      </div>
    );
  };


  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB");
    } catch (error) {
      return dateString;
    }
  };

  // Format date and time for audit trail
  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      const dateStr = date.toLocaleDateString("en-GB");
      const timeStr = date.toLocaleTimeString("en-GB", { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      });
      return `${dateStr} ${timeStr}`;
    } catch (error) {
      return dateString;
    }
  };

  // Download as PDF using jsPDF (Much more reliable for large documents)
  const handleDownloadPDF = () => {
    setDownloading(true);
    
    try {
      const appId = getQueryParam("appId");
      const now = new Date();
      const dateStr = `${now.getMonth() + 1}-${now.getDate()}-${now.getFullYear()}`;
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      const hour12 = (hours % 12 || 12).toString().padStart(2, "0");
      const timeStr = `${hour12}-${minutes}${ampm}`;
      const filename = `PreviewReport_${appId}-${dateStr}_${timeStr}.pdf`;

      // Initialize jsPDF
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      let yPosition = 20;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - 2 * margin;

      // Helper function to add page if needed
      const checkAndAddPage = (requiredSpace = 20) => {
        if (yPosition + requiredSpace > pageHeight - 20) {
          doc.addPage();
          yPosition = 20;
          return true;
        }
        return false;
      };

      // Add header with logo and title
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('National Accreditation Board for Testing and', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 7;
      doc.text('Calibration Laboratories', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      doc.setFontSize(14);
      doc.text('Preview Report', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;

      // Lab Details Section
      const labDetail = labDetailsState.length > 0 ? labDetailsState[0] : {};
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Name of Laboratory: ${labDetail.CabName || '-'}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Contact Person: ${labDetail.NablContPerson || '-'}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Mobile: ${labDetail.ContactPersonMobileNo || '-'}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Email: ${labDetail.ContactPersonEmail1 || '-'}`, margin, yPosition);
      yPosition += 10;

      // Section: Lab General Details
      checkAndAddPage(30);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setFillColor(227, 242, 253);
      doc.rect(margin, yPosition, contentWidth, 8, 'F');
      doc.text('Lab General Details', margin + 2, yPosition + 5);
      yPosition += 12;

      // Helper function to get observations data for a section
      const getObservationsData = (sectionName) => {
        if (!isObservationVisible) return { text: '-', isAdequate: null };
        
        const relevantObservations = observationsState.filter(
          (item) => item.SectionName?.toLowerCase() === sectionName.toLowerCase()
        );
        
        if (relevantObservations.length === 0) return { text: '-', isAdequate: null };
        
        // Get only the first matching observation record to avoid duplicates
        const obsData = relevantObservations[0];
        
        // Get the observation text and status
        const observation = obsData.Observation || '';
        const status = obsData.InformationStatus;
        const informationProvided = obsData.InformationProvided;
        
        // Check if it's "Document adequacy check completed" and status is Adequate
        const isAdequate = observation.trim().toLowerCase() === "document adequacy check completed" &&
                          status && status.toLowerCase() === "adequate";
        
        // If adequate, show "Adequate" in green
        if (isAdequate) {
          return { text: 'Adequate', isAdequate: true, status };
        }
        
        // If inadequate, show InformationProvided in red (or observation if no InformationProvided)
        if (status && status.toLowerCase() === "inadequate") {
          const text = informationProvided || observation || 'No information provided';
          return { text, isAdequate: false, status };
        }
        
        // Default: show observation
        return { text: observation || '-', isAdequate: null, status };
      };
      
      // Helper function to get observations text for backward compatibility
      const getObservationsText = (sectionName) => {
        return getObservationsData(sectionName).text;
      };

      // Helper function to build table row with conditional observation column
      const buildRow = (...values) => {
        return isObservationVisible ? [...values, ''] : values;
      };

      // Helper function to create table options with rowspan and color for observations
      const createTableWithObservations = (sectionName, bodyData) => {
        if (!isObservationVisible) {
          return {}; // Return empty object if observations are hidden
        }
        
        return {
          didParseCell: function(data) {
            // Only apply to body rows, not header
            if (data.section === 'body' && data.column.index === 2 && data.row.index === 0) {
              data.cell.rowSpan = bodyData.length;
            } else if (data.section === 'body' && data.column.index === 2 && data.row.index > 0) {
              data.cell.styles.fillColor = false;
              data.cell.text = [];
            }
          },
          didDrawCell: function(data) {
            // Only draw custom text in body, not in header
            if (data.section === 'body' && data.column.index === 2 && data.row.index === 0) {
              const obsData = getObservationsData(sectionName);
              if (obsData.isAdequate === true) {
                doc.setTextColor(0, 128, 0); // Green for adequate
              } else if (obsData.isAdequate === false) {
                doc.setTextColor(255, 0, 0); // Red for inadequate
              } else {
                doc.setTextColor(0, 0, 0); // Black for all other observations
              }
              
              const cellText = obsData.text;
              const lines = doc.splitTextToSize(cellText, data.cell.width - 4);
              doc.text(lines, data.cell.x + 2, data.cell.y + 4);
              doc.setTextColor(0, 0, 0); // Reset color
            }
          }
        };
      };

      // Lab Details Table - Complete with all fields
      const labDetailsData = [
        buildRow('Name of the CAB', labDetail.CabName || '-'),
        buildRow('Type of CAB', labDetail.CabType === 'Diagnostic' ? 'Medical Testing Laboratory' : 
                        labDetail.CabType === 'Medical Imaging' ? 'Medical Imaging' :
                        labDetail.CabType === 'Both' ? 'Medical Testing Laboratory + Medical Imaging' : '-'),
        buildRow('GSTIN', labDetail.GSTIN || '-'),
        buildRow('GST Exemption Certificate', labDetail.GSTExemptionFile || '-'),
        buildRow('PAN/TAN No.', labDetail.PANTANNumber || '-'),
        buildRow('Address', labDetail.Address || '-'),
        buildRow('Country', labDetail.CountryName || '-'),
        buildRow('State/Province', labDetail.StateName || '-'),
        buildRow('City', labDetail.CityName || '-'),
        buildRow('District', labDetail.DistrictName || '-'),
        buildRow('Sub District', labDetail.SubDistrictName || '-'),
        buildRow('Village/Town', labDetail.VillageTown || '-'),
        buildRow('Pin Code', labDetail.Pincode || '-'),
        buildRow('Mobile No', labDetail.MobileNo || '-'),
        buildRow('Landline No', labDetail.LandlineNo || '-'),
        buildRow('Fax', labDetail.Fax || '-'),
        buildRow('Fax ISD Code', labDetail.FaxIsdCode || '-'),
        buildRow('Email', labDetail.Email || '-'),
        buildRow('Are you from SEZ region (Special economic zone)', labDetail.IsFromSEZ ? 'Yes' : 'No'),
      ];

      // Add SEZ Certificate if applicable
      if (labDetail.IsFromSEZ) {
        labDetailsData.push(buildRow('SEZ Certificate', labDetail.SEZCertificateFile || '-'));
      }

      // Add consultancy details
      labDetailsData.push(buildRow('Whether any individual or organization has provided consultancy for preparing towards NABL Accreditation', labDetail.HasConsultancy ? 'Yes' : 'No'));
      
      if (labDetail.HasConsultancy) {
        labDetailsData.push(buildRow('Name of Individual/Organization with city', labDetail.ConsultancyName || '-'));
        labDetailsData.push(buildRow('Registered Office Address', labDetail.ConsultancyAddress || '-'));
      }

      // Add adverse action details
      labDetailsData.push(buildRow('Has adverse action has been initiated/taken against the laboratory in the past?', labDetail.HasAdverseAction ? 'Yes' : 'No'));
      
      if (labDetail.HasAdverseAction === 'Yes') {
        labDetailsData.push(buildRow('Type of adverse action', labDetail.AdverseActionType || '-'));
        labDetailsData.push(buildRow('Adverse Decision Date', formatDate(labDetail.AdverseDecisionDate) || '-'));
        labDetailsData.push(buildRow('Previous Lab Id from NABL', labDetail.PreviousLabId || '-'));
      }

      autoTable(doc, {
        startY: yPosition,
        head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
        body: labDetailsData,
        theme: 'grid',
        headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        columnStyles: isObservationVisible ? { 
          0: { cellWidth: 60 }, 
          1: { cellWidth: 60 }, 
          2: { cellWidth: 70 } 
        } : {
          0: { cellWidth: 70 }, 
          1: { cellWidth: 120 }
        },
        margin: { left: margin, right: margin },
        ...(isObservationVisible ? {
          didParseCell: function(data) {
            // Only apply to body rows, not header
            if (data.section === 'body' && data.column.index === 2 && data.row.index === 0) {
              data.cell.rowSpan = labDetailsData.length;
            } else if (data.section === 'body' && data.column.index === 2 && data.row.index > 0) {
              // Hide cells that are part of rowspan
              data.cell.styles.fillColor = false;
              data.cell.text = [];
            }
          },
          didDrawCell: function(data) {
            // Only draw custom text in body, not in header
            if (data.section === 'body' && data.column.index === 2 && data.row.index === 0) {
              const obsData = getObservationsData('Laboratory Details');
              if (obsData.isAdequate === true) {
                // Green for adequate
                doc.setTextColor(0, 128, 0);
              } else if (obsData.isAdequate === false) {
                // Red for inadequate
                doc.setTextColor(255, 0, 0);
              } else {
                // Black for all other observations
                doc.setTextColor(0, 0, 0);
              }
              
              // Draw text with color
              const cellText = obsData.text;
              const lines = doc.splitTextToSize(cellText, data.cell.width - 4);
              doc.text(lines, data.cell.x + 2, data.cell.y + 4);
              
              // Reset color
              doc.setTextColor(0, 0, 0);
            }
          }
        } : {})
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Legal Identity Details Section
      checkAndAddPage(30);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Legal Identity Details', margin, yPosition);
      yPosition += 8;

      const legalIdentityData = [
        buildRow('Legal Identity Type', labDetail.LegalIdentityType || '-'),
      ];

      // Add data based on legal identity type
      if (labDetail.LegalIdentityType === 'Proprietorship') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Name of the Owner', labDetail.OwnerName || '-'));
        legalIdentityData.push(buildRow('MCI/NMC Registration Number', labDetail.MCIRegNumber || '-'));
        legalIdentityData.push(buildRow('MCI/NMC Certificate', labDetail.LegalIdentityDoc1 || '-'));
        legalIdentityData.push(buildRow('State Registration Certificate Number', labDetail.StateRegistrationNumber || '-'));
        legalIdentityData.push(buildRow('State Registration Certificate', labDetail.LegalIdentityDoc2 || '-'));
      } else if (labDetail.LegalIdentityType === 'Partnership') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Partnership Deed', labDetail.CompaniesActCertificate || '-'));
        legalIdentityData.push(buildRow('Legal Identity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Name of the Owner', labDetail.OwnerName || '-'));
        legalIdentityData.push(buildRow('MCI/NMC Registration Number', labDetail.MCIRegNumber || '-'));
        legalIdentityData.push(buildRow('MCI/NMC Certificate', labDetail.LegalIdentityDoc1 || '-'));
        legalIdentityData.push(buildRow('State Registration Certificate Number', labDetail.StateRegistrationNumber || '-'));
        legalIdentityData.push(buildRow('State Registration Certificate', labDetail.LegalIdentityDoc2 || '-'));
      } else if (labDetail.LegalIdentityType === 'LLP') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Companies Act Certificate', labDetail.LLPCertificateFile || '-'));
      } else if (labDetail.LegalIdentityType === 'Companies Act') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Companies Act Certificate', labDetail.CompaniesActCertificateFile || '-'));
        legalIdentityData.push(buildRow('Address', labDetail.LegalIdentity_Address || '-'));
        legalIdentityData.push(buildRow('State', labDetail.LegalIdentity_State || '-'));
        legalIdentityData.push(buildRow('Pin Code', labDetail.CompaniesActPincode || '-'));
      } else if (labDetail.LegalIdentityType === 'Public Ltd') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Public Ltd Certificate', labDetail.PublicLtdCertificateFile || '-'));
        legalIdentityData.push(buildRow('Registered Office Address', labDetail.LegalIdentity_Address || '-'));
      } else if (labDetail.LegalIdentityType === 'Government') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Are you an Autonomous Body?', labDetail.GovernmentIsAutonomous ? 'Yes' : 'No'));
        legalIdentityData.push(buildRow('Gazette or Government Notification or self-Declaration on letter head by Head of the organization', labDetail.GovernmentCertificateFile || '-'));
      } else if (labDetail.LegalIdentityType === 'Trust') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Trust Certificate/Deed', labDetail.TrustCertificateFile || '-'));
      } else if (labDetail.LegalIdentityType === 'Society') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Society Certificate', labDetail.SocietyCertificateFile || '-'));
        legalIdentityData.push(buildRow('Are you an Autonomous Body?', labDetail.SocietyIsAutonomous ? 'Yes' : 'No'));
      } else if (labDetail.LegalIdentityType === 'One Person Company') {
        legalIdentityData.push(buildRow('Legal Entity Name', labDetail.LegalEntityName || '-'));
        legalIdentityData.push(buildRow('Registered Office Address', labDetail.LegalIdentity_Address || '-'));
        legalIdentityData.push(buildRow('One Person Company Certificate', labDetail.OPCCertificateFile || '-'));
      }

      autoTable(doc, {
        startY: yPosition,
        head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
        body: legalIdentityData,
        theme: 'grid',
        headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        columnStyles: isObservationVisible ? { 
          0: { cellWidth: 60 }, 
          1: { cellWidth: 60 }, 
          2: { cellWidth: 70 } 
        } : {
          0: { cellWidth: 70 }, 
          1: { cellWidth: 120 }
        },
        margin: { left: margin, right: margin },
        ...createTableWithObservations('Legal Entity Type', legalIdentityData)
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Type Of Functioning Section
      checkAndAddPage(30);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Type Of Functioning', margin, yPosition);
      yPosition += 8;

      const functioningData = [
        buildRow('Type Of Functioning', labDetail.TypeOfFunctioning || '-'),
      ];

      if (labDetail.TypeOfFunctioning === 'hospital-owned') {
        functioningData.push(buildRow('Name Of Hospital', labDetail.HospitalName || '-'));
        functioningData.push(buildRow('Upload Hospital Registration Certificate', labDetail.HospitalRegistrationCertificateFile || '-'));
        functioningData.push(buildRow('Type of Service Provider', labDetail.HospitalServiceProvider || '-'));
        functioningData.push(buildRow('Address of Hospital', labDetail.LegalIdentity_Address || '-'));
        functioningData.push(buildRow('State of Hospital', labDetail.LegalIdentity_State || '-'));
        functioningData.push(buildRow('Pincode of Hospital', labDetail.HospitalPincode || '-'));
      } else if (labDetail.TypeOfFunctioning === 'hospital-outsource') {
        functioningData.push(buildRow('Name Of Outsource Laboratory', labDetail.OutsourceLaboratoryName || '-'));
        functioningData.push(buildRow('Hospital and Laboratory MOU', labDetail.HospitalLabMouFile || '-'));
        functioningData.push(buildRow('Laboratory Address of Outsource Laboratory', labDetail.OutsourceLabAddress || '-'));
        functioningData.push(buildRow('State of Outsource Laboratory', labDetail.OutsourceLabState || '-'));
        functioningData.push(buildRow('Pincode of Outsource Laboratory', labDetail.OutsourceLabPincode || '-'));
        functioningData.push(buildRow('Type of Service Provider of Outsource Laboratory', labDetail.OutsourceServiceProvider || '-'));
      } else if (labDetail.TypeOfFunctioning === 'cro-lab') {
        functioningData.push(buildRow('Type of Service Provider', labDetail.CroServiceProvider || '-'));
      } else if (labDetail.TypeOfFunctioning === 'others') {
        functioningData.push(buildRow('Type of Service Provider', labDetail.OthersServiceProvider || '-'));
      }

      autoTable(doc, {
        startY: yPosition,
        head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
        body: functioningData,
        theme: 'grid',
        headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        columnStyles: isObservationVisible ? { 
          0: { cellWidth: 60 }, 
          1: { cellWidth: 60 }, 
          2: { cellWidth: 70 } 
        } : {
          0: { cellWidth: 70 }, 
          1: { cellWidth: 120 }
        },
        margin: { left: margin, right: margin },
        ...createTableWithObservations('Type Of Functioning', functioningData)
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Contact Person for NABL Section
      checkAndAddPage(30);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Contact Person for NABL', margin, yPosition);
      yPosition += 8;

      const contactPersonData = [
        buildRow('Contact Person for NABL', labDetail.NablContPerson || '-'),
        buildRow('Designation', labDetail.SpocDesig || '-'),
        buildRow('Mobile No', labDetail.ContactPersonMobileNo || '-'),
        buildRow('Landline No', labDetail.ContactPersonTelephoneNo || '-'),
        buildRow('Fax', labDetail.ContactPersonFaxNo || '-'),
        buildRow('Email 1', labDetail.ContactPersonEmail1 || '-'),
        buildRow('Email 2', labDetail.ContactPersonEmail2 || '-'),
      ];

      autoTable(doc, {
        startY: yPosition,
        head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
        body: contactPersonData,
        theme: 'grid',
        headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        columnStyles: isObservationVisible ? { 
          0: { cellWidth: 60 }, 
          1: { cellWidth: 60 }, 
          2: { cellWidth: 70 } 
        } : {
          0: { cellWidth: 70 }, 
          1: { cellWidth: 120 }
        },
        margin: { left: margin, right: margin },
        ...createTableWithObservations('Contact Person from laboratory for NABL', contactPersonData)
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Category of Facility Section
      checkAndAddPage(30);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Category of Facility(s) Applied Under the Scope of Accreditation', margin, yPosition);
      yPosition += 8;

      const categoryData = [
        buildRow('Permanant Facility', labDetail.PermanentFacility ? 'Yes' : 'No'),
        buildRow('Site Facility', labDetail.SiteFacility ? 'Yes' : 'No'),
        buildRow('POCT (Point of Care Testing)', labDetail.POCTFacility ? 'Yes' : 'No'),
        buildRow('Category of which accreditation is being sought', labDetail.AccreditationCategory || '-'),
      ];

      autoTable(doc, {
        startY: yPosition,
        head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
        body: categoryData,
        theme: 'grid',
        headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        columnStyles: isObservationVisible ? { 
          0: { cellWidth: 60 }, 
          1: { cellWidth: 60 }, 
          2: { cellWidth: 70 } 
        } : {
          0: { cellWidth: 70 }, 
          1: { cellWidth: 120 }
        },
        margin: { left: margin, right: margin },
        ...createTableWithObservations('Category of Facility(s) Applied Under the Scope of Accreditation', categoryData)
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Parent Organisation Details Section
      checkAndAddPage(30);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Parent Organisation Details', margin, yPosition);
      yPosition += 8;

      const parentOrgData = [
        buildRow('Name of the Parent Organization Applicable?', labDetail.IsParentOrgApplicable ? 'Yes' : 'No'),
      ];

      if (labDetail.IsParentOrgApplicable) {
        parentOrgData.push(buildRow('Name of the Parent Organization', labDetail.ParentOrgName || '-'));
        parentOrgData.push(buildRow('Parent Organization Contact No.', labDetail.ParentOrgContactNo || '-'));
        parentOrgData.push(buildRow('Parent Organization Landline No.', labDetail.ParentOrgLandlineNo || '-'));
        parentOrgData.push(buildRow('Parent Organization Fax No.', labDetail.ParentOrgFaxNo || '-'));
        parentOrgData.push(buildRow('Parent Organization Email Id', labDetail.ParentOrgEmailId || '-'));
      }

      autoTable(doc, {
        startY: yPosition,
        head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
        body: parentOrgData,
        theme: 'grid',
        headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        columnStyles: isObservationVisible ? { 
          0: { cellWidth: 60 }, 
          1: { cellWidth: 60 }, 
          2: { cellWidth: 70 } 
        } : {
          0: { cellWidth: 70 }, 
          1: { cellWidth: 120 }
        },
        margin: { left: margin, right: margin },
        ...createTableWithObservations('Parent Organisation Details', parentOrgData)
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Collection of Sample From Section
      checkAndAddPage(30);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Collection of Sample From', margin, yPosition);
      yPosition += 8;

      const sampleCollectionData = [
        buildRow('Sample Collection Centre(s)/ Facility(ies)', labDetail.SampleColletion ? 'Yes' : 'No'),
      ];

      if (labDetail.SampleColletion) {
        sampleCollectionData.push(buildRow('No of Collection Centres', labDetail.NumberOfCollectionCenters || '-'));
        sampleCollectionData.push(buildRow('Relevant Document', labDetail.SampleCollectionDocument || '-'));
      }

      sampleCollectionData.push(buildRow('Home collection', labDetail.HomeColletion ? 'Yes' : 'No'));
      
      if (labDetail.HomeColletion) {
        sampleCollectionData.push(buildRow('Relevant Document', labDetail.HomeCollectionDocument || '-'));
      }

      sampleCollectionData.push(buildRow('Other Source(s)', labDetail.OtherSource ? 'Yes' : 'No'));
      
      if (labDetail.OtherSource) {
        sampleCollectionData.push(buildRow('Relevant Document', labDetail.OtherSourcesDocument || '-'));
      }

      autoTable(doc, {
        startY: yPosition,
        head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
        body: sampleCollectionData,
        theme: 'grid',
        headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        columnStyles: isObservationVisible ? { 
          0: { cellWidth: 60 }, 
          1: { cellWidth: 60 }, 
          2: { cellWidth: 70 } 
        } : {
          0: { cellWidth: 70 }, 
          1: { cellWidth: 120 }
        },
        margin: { left: margin, right: margin },
        ...createTableWithObservations('Collection of Sample From', sampleCollectionData)
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Internal Audit and Management Review Section
      checkAndAddPage(30);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Internal Audit and Management Review', margin, yPosition);
      yPosition += 8;

      const auditData = [
        buildRow('Start Date of Last Internal Audit', formatDate(labDetail.InternalAuditFromDate) || '-'),
        buildRow('End Date of Last Internal Audit', formatDate(labDetail.InternalAuditToDate) || '-'),
        buildRow('Internal Audit Document', labDetail.InternalAuditDocument || '-'),
        buildRow('Whether all requirements of ISO 15189:2022 covering all activities of laboratory have been audited at least once in last one year?', labDetail.ISO15189Audited ? 'Yes' : 'No'),
        buildRow('Whether various locations (including collection centers) covered in the audit?', labDetail.LocationsAudited ? 'Yes' : 'No'),
        buildRow('Whether Pre and post examination activities included in the audit schedule?', labDetail.PrePostExamAudited ? 'Yes' : 'No'),
        buildRow('Start Date of Last Management Review', formatDate(labDetail.ManagementReviewFromDate) || '-'),
        buildRow('End Date of Last Management Review', formatDate(labDetail.ManagementReviewToDate) || '-'),
        buildRow('Management Review Document', labDetail.ManagementReviewDocument || '-'),
      ];

      autoTable(doc, {
        startY: yPosition,
        head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
        body: auditData,
        theme: 'grid',
        headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
        columnStyles: isObservationVisible ? { 
          0: { cellWidth: 60 }, 
          1: { cellWidth: 60 }, 
          2: { cellWidth: 70 } 
        } : {
          0: { cellWidth: 70 }, 
          1: { cellWidth: 120 }
        },
        margin: { left: margin, right: margin },
        ...createTableWithObservations('Internal Audit and Management Review', auditData)
      });

      yPosition = doc.lastAutoTable.finalY + 10;

      // Disciplines Section
      checkAndAddPage(30);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setFillColor(227, 242, 253);
      doc.rect(margin, yPosition, contentWidth, 8, 'F');
      doc.text('Disciplines', margin + 2, yPosition + 5);
      yPosition += 12;

      if (disciplineState.length > 0) {
        const disciplineData = disciplineState.map((discipline, index) => [
          index + 1,
          discipline.DisciplineName || '-',
          '-'
        ]);

        autoTable(doc, {
          startY: yPosition,
          head: [['Sr No.', 'Discipline Name']],
          body: disciplineData,
          theme: 'grid',
          headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
          styles: { fontSize: 7, cellPadding: 2 },
          margin: { left: margin, right: margin },
        });

        yPosition = doc.lastAutoTable.finalY + 10;
      }

     

       // Equipment Details Section
      if (equipmentDetailsState.length > 0) {
        checkAndAddPage(30);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(227, 242, 253);
        doc.rect(margin, yPosition, contentWidth, 8, 'F');
        doc.text('Equipment Details', margin + 2, yPosition + 5);
        yPosition += 12;

        // Process equipment in chunks to handle large datasets
        const chunkSize = 10; // Process 10 equipment items at a time
        for (let i = 0; i < equipmentDetailsState.length; i += chunkSize) {
          const chunk = equipmentDetailsState.slice(i, i + chunkSize);
          
          chunk.forEach((equipment, idx) => {
            checkAndAddPage(50);
            
            // Equipment item header
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text(`Equipment #${i + idx + 1}`, margin, yPosition);
            yPosition += 6;

            // Equipment details table with all fields
            const equipmentDetailsData = [
              buildRow('Discipline', equipment.DisciplineName || '-'),
              buildRow('Equipment Name', equipment.NameOfEquipment || '-'),
              buildRow('Equipment ID', equipment.Equipmentid || equipment.EquipmentId || '-'),
              buildRow('UID', equipment.UID || equipment.Uid || '-'),
              buildRow('Model', equipment.Model || '-'),
              buildRow('Serial No', equipment.SerialNumber || equipment.SerialNo || '-'),
              buildRow('Year of Make', equipment.YearOfMake || equipment.ManufacturingYear || '-'),
              buildRow('Receipt Date & Date Placed in Service', (equipment.ReceiptDate ? formatDate(equipment.ReceiptDate) : (equipment.DatePlacedInService ? formatDate(equipment.DatePlacedInService) : '-'))),
              buildRow('Calibration Date', (equipment.CalibrationDate ? formatDate(equipment.CalibrationDate) : (equipment.LastCalibrationDate ? formatDate(equipment.LastCalibrationDate) : (equipment.DateOfLastCalibration ? formatDate(equipment.DateOfLastCalibration) : '-')))),
              buildRow('Calibration Due on', (equipment.CalibrationDueDate ? formatDate(equipment.CalibrationDueDate) : (equipment.NextCalibrationDate ? formatDate(equipment.NextCalibrationDate) : (equipment.CalibrationDueOn ? formatDate(equipment.CalibrationDueOn) : '-')))),
              buildRow('Calibrated By', equipment.CalibratedBy || equipment.CalibrationType || equipment.CalibrationBy || '-'),
              buildRow('Name of Calibration Agency/External Laboratory', equipment.CalibrationAgency || equipment.ExternalLaboratory || equipment.CalibrationAgencyName ||equipment.ExternalCalibrationAgency || '-'),
              buildRow('Date of Installation', (equipment.InstallationDate ? formatDate(equipment.InstallationDate) : (equipment.DateOfInstallation ? formatDate(equipment.DateOfInstallation) : '-'))),
              buildRow('In-house/Traceability', equipment.Traceability || equipment.InHouseTraceability || equipment.InHouseCalibration || '-'),
              buildRow('Name of Manufacturer', equipment.Make || equipment.MakeCompany || equipment.ManufacturerName || '-'),
              buildRow('Limit of Detection', equipment.LimitOfDetection || equipment.LOD || equipment.RangeCapacity || '-'),
            ];

            autoTable(doc, {
              startY: yPosition,
              head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
              body: equipmentDetailsData,
              theme: 'grid',
              headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
              styles: { 
                fontSize: 7, 
                cellPadding: 1.5,
                overflow: 'linebreak'
              },
              columnStyles: isObservationVisible ? { 
                0: { cellWidth: 50, fontStyle: 'bold' }, 
                1: { cellWidth: 80 },
                2: { cellWidth: 60 }
              } : {
                0: { cellWidth: 70, fontStyle: 'bold' }, 
                1: { cellWidth: 120 }
              },
              margin: { left: margin, right: margin },
              ...createTableWithObservations('Equipment Details', equipmentDetailsData)
            });

            yPosition = doc.lastAutoTable.finalY + 5;
          });
        }

        yPosition += 5; // Extra space after equipment section
      }

        // Reference Materials Section
      if (referenceMaterialsState.length > 0) {
        checkAndAddPage(30);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(227, 242, 253);
        doc.rect(margin, yPosition, contentWidth, 8, 'F');
        doc.text('Reference Material Details', margin + 2, yPosition + 5);
        yPosition += 12;

        // Process reference materials in chunks
        const chunkSize = 10;
        for (let i = 0; i < referenceMaterialsState.length; i += chunkSize) {
          const chunk = referenceMaterialsState.slice(i, i + chunkSize);
          
          chunk.forEach((material, idx) => {
            checkAndAddPage(40);
            
            // Reference material item header
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text(`Reference Material #${i + idx + 1}`, margin, yPosition);
            yPosition += 6;

            // Reference material details table
            const refMaterialData = [
              buildRow('Discipline', material.DisciplineName || '-'),
              buildRow('Name of Reference Material/Strain/Culture', material.Name || '-'),
              buildRow('Source', material.Source || '-'),
              buildRow('Date of Expiry', (material.DateOfExpiry && formatDate(material.DateOfExpiry) !== "01/01/1900" ? formatDate(material.DateOfExpiry) : '-')),
              buildRow('Traceability', material.Traceability || '-'),
            ];

            autoTable(doc, {
              startY: yPosition,
              head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
              body: refMaterialData,
              theme: 'grid',
              headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
              styles: { 
                fontSize: 7, 
                cellPadding: 1.5,
                overflow: 'linebreak'
              },
              columnStyles: isObservationVisible ? { 
                0: { cellWidth: 50, fontStyle: 'bold' }, 
                1: { cellWidth: 80 },
                2: { cellWidth: 60 }
              } : {
                0: { cellWidth: 70, fontStyle: 'bold' }, 
                1: { cellWidth: 120 }
              },
              margin: { left: margin, right: margin },
              ...createTableWithObservations('Reference Material', refMaterialData)
            });

            yPosition = doc.lastAutoTable.finalY + 5;
          });
        }

        yPosition += 5;
      }
       // Scope Section - Detailed format matching UI
      checkAndAddPage(30);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.setFillColor(227, 242, 253);
      doc.rect(margin, yPosition, contentWidth, 8, 'F');
      doc.text('Proposed Scope', margin + 2, yPosition + 5);
      yPosition += 12;

      if (scopeState.length > 0) {
        // Process scope items individually with all details
        scopeState.forEach((scope, idx) => {
          checkAndAddPage(60);

          const scopeDetailsData = [
            buildRow('Discipline', scope.DisciplineName || '-'),
            buildRow('Product/Material of test', scope.GroupTested || '-'),
            buildRow('Specific Test Performed', scope.TypeOfTest || '-'),
            buildRow('Test method', scope.ScopeStandard || '-'),
            buildRow('Range Type', scope.rangeType || '-'),
            buildRow('Range of Testing/Limit of detection', `${scope.RangeLL || '-'} ${scope.RangeLLUnit || '-'} To ${scope.RangeUL || '-'} ${scope.RangeULUnit || '-'}`),
            buildRow('(±)MU @Value', scope.mu || '-'),
            buildRow('Test Performed At', scope.testPerformedAt || '-'),
            buildRow('Test code', scope.testCode || '-'),
            buildRow('Range Remarks', scope.rangeRemark || '-'),
            buildRow('CV Value', scope.cv || '-'),
            buildRow('PT Availability', scope.PTAvailable || '-'),
            buildRow('Equipment', scope.EquipmentName || '-'),
            buildRow('Reference Material', scope.Ref_Id || '-'),
            buildRow('Proficiency Testing', scope.Pt_id || '-'),
          ];

          autoTable(doc, {
            startY: yPosition,
            head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
            body: scopeDetailsData,
            theme: 'grid',
            headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
            styles: { fontSize: 7, cellPadding: 1.5, overflow: 'linebreak' },
            columnStyles: isObservationVisible ? { 
              0: { cellWidth: 50 }, 
              1: { cellWidth: 80 },
              2: { cellWidth: 60 }
            } : {
              0: { cellWidth: 70 }, 
              1: { cellWidth: 120 }
            },
            margin: { left: margin, right: margin },
            ...createTableWithObservations('Scope', scopeDetailsData)
          });

          yPosition = doc.lastAutoTable.finalY + 5;
        });
      }
  if (organisationDetailsState.length > 0) {
        checkAndAddPage(30);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(227, 242, 253);
        doc.rect(margin, yPosition, contentWidth, 8, 'F');
        doc.text('Organisation Details', margin + 2, yPosition + 5);
        yPosition += 12;

        // Process organization data in chunks
        const chunkSize = 10;
        for (let i = 0; i < organisationDetailsState.length; i += chunkSize) {
          const chunk = organisationDetailsState.slice(i, i + chunkSize);
          
          chunk.forEach((org, idx) => {
            checkAndAddPage(60);
            
            // Staff item header
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text(`Staff Member #${i + idx + 1}`, margin, yPosition);
            yPosition += 6;

            // Staff details table
            const staffDetailsData = [
              buildRow('Discipline', org.DisciplineName || '-'),
              buildRow('Employee Type', org.EmployeeType || '-'),
              buildRow('Name', org.Name || '-'),
              buildRow('Designation', org.Designation || '-'),
              buildRow('Qualification', org.Qualification || '-'),
              buildRow('Specialization', org.Specialization || '-'),
              buildRow('Experience (Years)', org.RelevantExperience || org.Experience || '-'),
              buildRow('Relevant Training', org.RelevantTraining || '-'),
              buildRow('Landline No', org.LandlineNo || '-'),
              buildRow('Mobile No', org.ContactNo || '-'),
              buildRow('Email', org.EmailID || '-'),
              buildRow('Engagement Type', org.typeofEngagement || '-'),
              buildRow('Timing', org.Timming || '-'),
              buildRow('Authorized Signatory', org.IsAuthorisedSignatory ? 'Yes' : 'No'),
              buildRow('Authorized Signatory File', org.SignatorySign || '-'),
            ];

            autoTable(doc, {
              startY: yPosition,
              head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
              body: staffDetailsData,
              theme: 'grid',
              headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
              styles: { 
                fontSize: 7, 
                cellPadding: 1.5,
                overflow: 'linebreak'
              },
              columnStyles: isObservationVisible ? { 
                0: { cellWidth: 50, fontStyle: 'bold' }, 
                1: { cellWidth: 80 },
                2: { cellWidth: 60 }
              } : {
                0: { cellWidth: 70, fontStyle: 'bold' }, 
                1: { cellWidth: 120 }
              },
              margin: { left: margin, right: margin },
              ...createTableWithObservations('Staff', staffDetailsData)
            });

            yPosition = doc.lastAutoTable.finalY + 5;
          });
        }

        yPosition += 5;
      }
     

      // Organization Details Section
    

    

      // Non Analytical Instruments Section
      if (nonAnalyticalInstrumentsState.length > 0) {
        checkAndAddPage(30);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(227, 242, 253);
        doc.rect(margin, yPosition, contentWidth, 8, 'F');
        doc.text('Non Analytical Instruments Details', margin + 2, yPosition + 5);
        yPosition += 12;

        // Process instruments in chunks
        const chunkSize = 10;
        for (let i = 0; i < nonAnalyticalInstrumentsState.length; i += chunkSize) {
          const chunk = nonAnalyticalInstrumentsState.slice(i, i + chunkSize);
          
          chunk.forEach((instrument, idx) => {
            checkAndAddPage(35);
            
            // Instrument item header
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text(`Non Analytical Instrument #${i + idx + 1}`, margin, yPosition);
            yPosition += 6;

            // Instrument details table
            const instrumentData = [
              buildRow('Instrument Name', instrument.InstrumentName ? String(instrument.InstrumentName).replace(/^[,\s]+|[,\s]+$/g, '').trim() : '-'),
              buildRow('UID', instrument.UID || '-'),
              buildRow('Calibration Frequency', instrument.Calibration_Frequency || '-'),
              buildRow('Last Calibration Date', formatDate(instrument.LastCalibration_Date) || '-'),
              buildRow('Next Calibration Date', formatDate(instrument.NextCalibration_Date) || '-'),
            ];

            autoTable(doc, {
              startY: yPosition,
              head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
              body: instrumentData,
              theme: 'grid',
              headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
              styles: { 
                fontSize: 7, 
                cellPadding: 1.5,
                overflow: 'linebreak'
              },
              columnStyles: isObservationVisible ? { 
                0: { cellWidth: 50, fontStyle: 'bold' }, 
                1: { cellWidth: 80 },
                2: { cellWidth: 60 }
              } : {
                0: { cellWidth: 70, fontStyle: 'bold' }, 
                1: { cellWidth: 120 }
              },
              margin: { left: margin, right: margin },
              ...createTableWithObservations('Non-Analytical Instruments', instrumentData)
            });

            yPosition = doc.lastAutoTable.finalY + 5;
          });
        }

        yPosition += 5;
      }

      // Phantoms Section (for Medical Imaging labs)
      if ((labDetail.CabType === 'Medical Imaging' || labDetail.CabType === 'Both') && form153PhantomsState.length > 0) {
        checkAndAddPage(30);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(227, 242, 253);
        doc.rect(margin, yPosition, contentWidth, 8, 'F');
        doc.text('Phantoms Details', margin + 2, yPosition + 5);
        yPosition += 12;

        // Process phantoms in chunks
        const chunkSize = 10;
        for (let i = 0; i < form153PhantomsState.length; i += chunkSize) {
          const chunk = form153PhantomsState.slice(i, i + chunkSize);
          
          chunk.forEach((phantom, idx) => {
            checkAndAddPage(40);
            
            // Phantom item header
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text(`Phantom #${i + idx + 1}`, margin, yPosition);
            yPosition += 6;

            // Phantom details table
            const phantomData = [
              buildRow('Phantom Type', phantom.PhantomType || '-'),
              buildRow('Discipline', phantom.DisciplineName || '-'),
              buildRow('Type of Test', phantom.TypeOfTestNames || '-'),
              buildRow('Specifications', phantom.Specifications || '-'),
              buildRow('Date of Placing', formatDate(phantom.DateofPlacing) || '-'),
              buildRow('Traceability', phantom.Traceability || '-'),
            ];

            autoTable(doc, {
              startY: yPosition,
              head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
              body: phantomData,
              theme: 'grid',
              headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
              styles: { 
                fontSize: 7, 
                cellPadding: 1.5,
                overflow: 'linebreak'
              },
              columnStyles: isObservationVisible ? { 
                0: { cellWidth: 50, fontStyle: 'bold' }, 
                1: { cellWidth: 80 },
                2: { cellWidth: 60 }
              } : {
                0: { cellWidth: 70, fontStyle: 'bold' }, 
                1: { cellWidth: 120 }
              },
              margin: { left: margin, right: margin },
              ...createTableWithObservations('phantoms', phantomData)
            });

            yPosition = doc.lastAutoTable.finalY + 5;
          });
        }

        yPosition += 5;
      }

      // Radioisotopes/Test Kits Section (for Medical Imaging labs)
      if ((labDetail.CabType === 'Medical Imaging' || labDetail.CabType === 'Both') && radioDetailsState.length > 0) {
        checkAndAddPage(30);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setFillColor(227, 242, 253);
        doc.rect(margin, yPosition, contentWidth, 8, 'F');
        doc.text('Radioisotopes/Test Kits Details', margin + 2, yPosition + 5);
        yPosition += 12;

        // Process radioisotopes in chunks
        const chunkSize = 10;
        for (let i = 0; i < radioDetailsState.length; i += chunkSize) {
          const chunk = radioDetailsState.slice(i, i + chunkSize);
          
          chunk.forEach((radio, idx) => {
            checkAndAddPage(45);
            
            // Radio item header
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            doc.text(`Radioisotope/Test Kit #${i + idx + 1}`, margin, yPosition);
            yPosition += 6;

            // Radio details table
            const radioData = [
              buildRow('Name of Radioisotopes', radio.NameofRadioisotopes || '-'),
              buildRow('Discipline', radio.DisciplineName || '-'),
              buildRow('Type of Test', radio.TypeOfTestNames || '-'),
              buildRow('Half Life', radio.HalfLife || '-'),
              buildRow('Energy (KeV)', radio.EnergyKeV || '-'),
              buildRow('Max Activity Permitted', radio.MaxactivityPermitted || '-'),
              buildRow('Details of Supplier', radio.DetailsofSupplier || '-'),
            ];

            autoTable(doc, {
              startY: yPosition,
              head: isObservationVisible ? [['Details', 'Data Feeded by Lab', 'Observations']] : [['Details', 'Data Feeded by Lab']],
              body: radioData,
              theme: 'grid',
              headStyles: { fillColor: [219, 216, 216], textColor: [0, 0, 0], fontStyle: 'bold' },
              styles: { 
                fontSize: 7, 
                cellPadding: 1.5,
                overflow: 'linebreak'
              },
              columnStyles: isObservationVisible ? { 
                0: { cellWidth: 50, fontStyle: 'bold' }, 
                1: { cellWidth: 80 },
                2: { cellWidth: 60 }
              } : {
                0: { cellWidth: 70, fontStyle: 'bold' }, 
                1: { cellWidth: 120 }
              },
              margin: { left: margin, right: margin },
              ...createTableWithObservations('radioisotopes', radioData)
            });

            yPosition = doc.lastAutoTable.finalY + 5;
          });
        }

        yPosition += 5;
      }

      // Add footer with page numbers
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.text(
          `Page ${i} of ${totalPages}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
        doc.text(
          `Generated on: ${new Date().toLocaleString()}`,
          pageWidth - margin,
          pageHeight - 10,
          { align: 'right' }
        );
      }

      // Save the PDF
      doc.save(filename);
      
      console.log(`✅ PDF generated successfully with ${totalPages} pages`);
      alert(`PDF generated successfully with ${totalPages} pages!`);
      
    } catch (error) {
      console.error("PDF generation error:", error);
      alert("Error generating PDF. Please check console for details.");
    } finally {
      setDownloading(false);
    }
  };

  // Get the first lab detail (assuming there's at least one)
  const labDetail = labDetailsState.length > 0 ? labDetailsState[0] : {};


  const viewS3File = async (fileName) => {
    try {
      const url = await s3Utils.generateSignedUrlForFilename(fileName);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error("Error generating S3 URL:", error);
    }
  };

  return (
    <Box sx={{ p: 3, mt: 7, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      {/* PDF-specific styles */}
      <style>{`
        @media print {
          #pdf-report-content {
            page-break-inside: avoid;
          }
          .page-break-before {
            page-break-before: always !important;
            break-before: always !important;
          }
          .page-break-after {
            page-break-after: always !important;
            break-after: always !important;
          }
          table {
            page-break-inside: auto !important;
          }
          tr {
            page-break-inside: avoid !important;
            page-break-after: auto !important;
          }
          td {
            page-break-inside: avoid !important;
          }
          thead {
            display: table-header-group !important;
          }
          tfoot {
            display: table-footer-group !important;
          }
        }
        #pdf-report-content table {
          page-break-inside: auto;
        }
        #pdf-report-content tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }
        #pdf-report-content td {
          page-break-inside: avoid;
        }
      `}</style>
      
      <div id="pdf-report-content">
        <Paper elevation={3} sx={{ p: 2, mb: 2, mt: 1, borderRadius: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Logo and Header */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <img
                src="/main/images/v1_9old.png"
                alt="NABL Logo"
                style={{ height: 70, marginRight: 24 }}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h7"
                  sx={{ fontWeight: "bold", color: "primary.main", mb: 0.5 }}
                >
                  National Accreditation Board for Testing and Calibration
                  Laboratories
                </Typography>
                <Typography
                  variant="h9"
                  sx={{ color: "text.secondary", fontWeight: "bold" }}
                >
                  Preview Report
                </Typography>
              </Box>
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Name of Laboratory:&nbsp;
                <span style={{ fontWeight: 400 }}>
                  {labDetail.CabName || "-"}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Contact Person:&nbsp;
                <span style={{ fontWeight: 400 }}>
                  {labDetail.NablContPerson || "-"}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Mobile:&nbsp;
                <span style={{ fontWeight: 400 }}>
                  {labDetail.ContactPersonMobileNo || "-"}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Email:&nbsp;
                <span style={{ fontWeight: 400 }}>
                  {labDetail.ContactPersonEmail1 || "-"}
                </span>
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        <Box sx={{display:'none'}}>
        {/* Laboratory Details Section */}
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
        >
          Lab General Details
        </Typography>

        {/* Subsection: Name of the CAB */}
        <TableContainer
          component={Paper}
          sx={{ mb: 2, maxWidth: "100%", mx: "auto", borderRadius: 0 }}
        >
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": {
                border: "1px solid #bdbdbd",
              },
              borderCollapse: "collapse",
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                <TableCell align="right" sx={{ width: "50%" }}>
                  <strong>Details</strong>
                </TableCell>
                <TableCell align="left" sx={{ width: "50%" }}>
                  <strong>Data Feeded by Lab</strong>
                </TableCell>
                {isObservationVisible && (
                  <TableCell align="center" sx={{ width: "30%" }}>
                    <strong>Observation</strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Name of the CAB
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.CabName || "-"}
                </TableCell>
                {isObservationVisible && (
                  <TableCell
                    align="center"
                    sx={{ width: "30%", fontSize: "0.82rem" }}
                    rowSpan={17}
                    style={{ verticalAlign: "top" }}
                  >
                    {renderObservationCell(getObservationBySection("Laboratory Details", null))}
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Type of CAB
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.CabType === "Diagnostic"
                    ? "Medical Testing Laboratory"
                    : labDetail.CabType === "Medical Imaging"
                    ? "Medical Imaging"
                    : labDetail.CabType === "Both"
                    ? "Medical Testing Laboratory + Medical Imaging"
                    : "-"
                  }
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  GSTIN
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.GSTIN || "-"}
                </TableCell>
              </TableRow>
               <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  GST Exemption Certificate
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.GSTExemptionFile ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.GSTExemptionFile);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  PAN/TAN No.
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.PANTANNumber || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Address
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.Address || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Country
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.CountryName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  State/Province
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.StateName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  City
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.CityName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  District
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.DistrictName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Sub District
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.SubDistrictName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Village/Town 
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.VillageTown || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Pin Code
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.Pincode || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Mobile No
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.MobileNo || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Landline No
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LandlineNo || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Fax
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.Fax || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Fax ISD Code
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.FaxIsdCode || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Email
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.Email || "-"}
                </TableCell>
              </TableRow>              
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Are you from SEZ region (Special economic zone)
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.IsFromSEZ ? "Yes" : "No"}
                </TableCell>
              </TableRow>
              {labDetail.IsFromSEZ && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  SEZ Certificate  
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.SEZCertificateFile ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.SEZCertificateFile);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              </>
              )}
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Whether any individual or organization has provided
                  consultancy for preparing towards NABL Accreditation
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.HasConsultancy ? "Yes" : "No"}
                </TableCell>
              </TableRow>
              {labDetail.HasConsultancy && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Name of Individual/Organization with city
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.ConsultancyName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Registered Office Address
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.ConsultancyAddress || "-"}
                </TableCell>
              </TableRow>
              </>
              )}
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Has adverse action has been initiated/taken against the laboratory in the past?
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.HasAdverseAction ? "Yes" : "No"}
                </TableCell>
              </TableRow>
              {labDetail.HasAdverseAction === "Yes" && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Type of adverse action
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.AdverseActionType || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Adverse Decision Date
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.AdverseDecisionDate ? formatDate(labDetail.AdverseDecisionDate) : "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Previous Lab Id from NABL
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.PreviousLabId || "-"}
                </TableCell>
              </TableRow>
              </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Subsection: Legal Identity Details */}
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
        >
          Legal Identity Details
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": {
                border: "1px solid #bdbdbd",
              },
              borderCollapse: "collapse",
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Legal Identity Type
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalIdentityType || "-"}
                </TableCell>
                {isObservationVisible && (
                  <TableCell
                    align="center"
                    sx={{ fontSize: "0.82rem" }}
                    rowSpan={8}
                    style={{ verticalAlign: "top" }}
                  >
                    {renderObservationCell(getObservationBySubSection("Legal Entity Type", "legal-entity-type"))}
                  </TableCell>
                )}
              </TableRow>
              {labDetail.LegalIdentityType === "Proprietorship" && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Legal Entity Name
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalEntityName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Name of the Owner
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.OwnerName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  MCI/NMC Registration Number
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.MCIRegNumber || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  MCI/NMC Certificate
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalIdentityDoc1 ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.LegalIdentityDoc1);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  State Registration Certificate Number
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.StateRegistrationNumber || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  State Registration Certificate
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalIdentityDoc2 ? (
                   <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.LegalIdentityDoc2);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              </>
              )}
              {labDetail.LegalIdentityType === "Partnership" && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Legal Entity Name
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalEntityName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Partnership Deed
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.CompaniesActCertificate ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.CompaniesActCertificate);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Legal Identity Name
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalEntityName || "No"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Name of the Owner
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.OwnerName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  MCI/NMC Registration Number
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.MCIRegNumber || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  MCI/NMC Certificate
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalIdentityDoc1 ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.LegalIdentityDoc1);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  State Registration Certificate Number
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.StateRegistrationNumber || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  State Registration Certificate
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalIdentityDoc2 ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.LegalIdentityDoc2);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              </>
              )}
              {labDetail.LegalIdentityType === "LLP" && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Legal Entity Name
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalEntityName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Companies Act Certificate
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LLPCertificateFile ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.LLPCertificateFile);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              </>
              )}
              {labDetail.LegalIdentityType === "Companies Act" && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Legal Entity Name
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalEntityName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Companies Act Certificate
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.CompaniesActCertificateFile ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.CompaniesActCertificateFile);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>         
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Address
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalIdentity_Address || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  State
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalIdentity_State || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Pin Code
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.CompaniesActPincode || "-"}
                </TableCell>
              </TableRow>
              </>
              )}
              {labDetail.LegalIdentityType === "Public Ltd" && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Legal Entity Name
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalEntityName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Public Ltd Certificate
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.PublicLtdCertificateFile ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.PublicLtdCertificateFile);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                 Registered Office Address
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalIdentity_Address || "No"}
                </TableCell>
              </TableRow>           
              </>
              )}
              {labDetail.LegalIdentityType === "Government" && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Legal Entity Name
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalEntityName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Are you an Autonomous Body?
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.GovernmentIsAutonomous?"Yes":"No" }
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Gazette or Government Notification or self-Declaration on letter head by Head of the organization
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.GovernmentCertificateFile ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.GovernmentCertificateFile);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>           
              </>
              )}
              {labDetail.LegalIdentityType === "Trust" && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Legal Entity Name
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalEntityName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Trust Certificate/Deed
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.TrustCertificateFile ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.TrustCertificateFile);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              </>
              )}
              {labDetail.LegalIdentityType === "Society" && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Legal Entity Name
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalEntityName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Society Certificate
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.SocietyCertificateFile ? (
                   <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.SocietyCertificateFile);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Are you an Autonomous Body?
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.SocietyIsAutonomous? "Yes":"No"}
                </TableCell>
              </TableRow>
              </>
              )}
              {labDetail.LegalIdentityType === "One Person Company" && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Legal Entity Name
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalEntityName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Registered Office Address
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalIdentity_Address || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  One Person Company Certificate
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.OPCCertificateFile ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.OPCCertificateFile);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Subsection: Type Of Functioning */}
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
        >
          Type Of Functioning
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": {
                border: "1px solid #bdbdbd",
              },
              borderCollapse: "collapse",
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Type Of Functioning
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.TypeOfFunctioning || "-"}
                </TableCell>
                {isObservationVisible && (
                  <TableCell
                    align="center"
                    sx={{ fontSize: "0.82rem" }}
                    rowSpan={8}
                    style={{ verticalAlign: "top" }}
                  >
                    {renderObservationCell(getObservationBySubSection("Type of Functioning", "type-of-functioning"))}
                  </TableCell>
                )}
              </TableRow>
              {labDetail.TypeOfFunctioning === "hospital-owned" && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Name Of Hospital
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.HospitalName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Upload Hospital Registration Certificate
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.HospitalRegistrationCertificateFile ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.HospitalRegistrationCertificateFile);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Type of Service Provider
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.HospitalServiceProvider || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Address of Hospital
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalIdentity_Address || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  State of Hospital
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LegalIdentity_State || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Pincode of Hospital
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.HospitalPincode || "No"}
                </TableCell>
              </TableRow>
              </>
              )}
              {labDetail.TypeOfFunctioning === "hospital-outsource" && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Name Of Outsource Laboratory
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.OutsourceLaboratoryName || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Hospital and Laboratory MOU
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.HospitalLabMouFile ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.HospitalLabMouFile);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Laboratory Address of Outsource Laboratory
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.OutsourceLabAddress || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  State of Outsource Laboratory
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.OutsourceLabState || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Pincode of Outsource Laboratory
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.OutsourceLabPincode || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Type of Service Provider of Outsource Laboratory
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.OutsourceServiceProvider || "-"}
                </TableCell>
              </TableRow>
              </>
              )}
              {labDetail.TypeOfFunctioning === "cro-lab" && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Type of Service Provider
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.CroServiceProvider || "-"}
                </TableCell>
              </TableRow>
              </>
              )}
              {labDetail.TypeOfFunctioning === "others" && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Type of Service Provider
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.OthersServiceProvider || "-"}
                </TableCell>
              </TableRow>
              </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Subsection: Contact Person for NABL */}
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
        >
          Contact Person for NABL
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": {
                border: "1px solid #bdbdbd",
              },
              borderCollapse: "collapse",
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Contact Person for NABL
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.NablContPerson || "-"}
                </TableCell>
                {isObservationVisible && (
                  <TableCell
                    align="center"
                    sx={{ fontSize: "0.82rem" }}
                    rowSpan={6}
                    style={{ verticalAlign: "top" }}
                  >
                    {renderObservationCell(getObservationBySubSection("Contact Person from laboratory for NABL", "contact-person-nabl"))}
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Designation
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.SpocDesig || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Mobile No
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.ContactPersonMobileNo || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Landline No
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.ContactPersonTelephoneNo || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Fax
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.ContactPersonFaxNo || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Email 1
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.ContactPersonEmail1 || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Email 2
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.ContactPersonEmail2 || "-"}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Subsection: Category of Facility(s) Applied Under the Scope of Accreditation */}
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
        >
          Category of Facility(s) Applied Under the Scope of Accreditation
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": {
                border: "1px solid #bdbdbd",
              },
              borderCollapse: "collapse",
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Permanant Facility
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.PermanentFacility ? "Yes" : "No"}
                </TableCell>
                {isObservationVisible && (
                  <TableCell
                    align="center"
                    sx={{ fontSize: "0.82rem" }}
                    rowSpan={4}
                    style={{ verticalAlign: "top" }}
                  >
                    {renderObservationCell(getObservationBySubSection("Category of Facility", "category-of-facility"))}
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Site Facility
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.SiteFacility ? "Yes" : "No"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  POCT (Point of Care Testing)
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.POCTFacility ? "Yes" : "No"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Category of which accreditation is being sought
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.AccreditationCategory || "-"}
                </TableCell>
              </TableRow>
              {/* <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Mobile Facility
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.MobileFacility ? "Yes" : "No"}
                </TableCell>
              </TableRow> */}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Subsection: Parent Organisation Details */}
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
        >
          Parent Organisation Details
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": {
                border: "1px solid #bdbdbd",
              },
              borderCollapse: "collapse",
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Name of the Parent Organization Applicable?
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.IsParentOrgApplicable ? "Yes" : "No"}
                </TableCell>
                {isObservationVisible && (
                  <TableCell
                    align="center"
                    sx={{ fontSize: "0.82rem" }}
                    rowSpan={labDetail.IsParentOrgApplicable ? 1 : 5}
                    style={{ verticalAlign: "top" }}
                  >
                    {renderObservationCell(getObservationBySubSection("Parent Organization", "parent-organization"))}
                  </TableCell>
                )}
              </TableRow>
              {labDetail.IsParentOrgApplicable == true && (
                <>
                  <TableRow>
                    <TableCell
                      align="right"
                      sx={{ width: "50%", fontSize: "0.82rem" }}
                    >
                      Name of the Parent Organization
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ width: "50%", fontSize: "0.82rem" }}
                    >
                      {labDetail.ParentOrgName || "-"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      align="right"
                      sx={{ width: "50%", fontSize: "0.82rem" }}
                    >
                      Parent Organization Contact No.
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ width: "50%", fontSize: "0.82rem" }}
                    >
                      {labDetail.ParentOrgContactNo || "-"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      align="right"
                      sx={{ width: "50%", fontSize: "0.82rem" }}
                    >
                      Parent Organization Landline No.
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ width: "50%", fontSize: "0.82rem" }}
                    >
                      {labDetail.ParentOrgLandlineNo || "-"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      align="right"
                      sx={{ width: "50%", fontSize: "0.82rem" }}
                    >
                      Parent Organization Fax No.
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ width: "50%", fontSize: "0.82rem" }}
                    >
                      {labDetail.ParentOrgFaxNo || "-"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      align="right"
                      sx={{ width: "50%", fontSize: "0.82rem" }}
                    >
                      Parent Organization Email Id
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ width: "50%", fontSize: "0.82rem" }}
                    >
                      {labDetail.ParentOrgEmailId || "-"}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

 {/* Subsection: Collection of Sample From */}
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
        >
          Collection of Sample From
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": {
                border: "1px solid #bdbdbd",
              },
              borderCollapse: "collapse",
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Sample Collection Centre(s)/ Facility(ies)
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.SampleColletion ? "Yes" : "No"}
                </TableCell>
                {isObservationVisible && (
                  <TableCell
                    align="center"
                    sx={{ fontSize: "0.82rem" }}
                    rowSpan={7}
                    style={{ verticalAlign: "top" }}
                  >
                    {renderObservationCell(getObservationBySubSection("Collection of Sample From", "sample-collection"))}
                  </TableCell>
                )}
              </TableRow>
              {labDetail.SampleColletion && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  No of Collection Centres
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.NumberOfCollectionCenters || "-"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Relevant Document
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.SampleCollectionDocument ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.SampleCollectionDocument);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              </>
              )}
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Home collection
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.HomeColletion ? "Yes" : "No"}
                </TableCell>
              </TableRow>
              {labDetail.HomeColletion && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Relevant Document
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.HomeCollectionDocument ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.HomeCollectionDocument);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              </>
              )}
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Other Source(s)
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.OtherSource ? "Yes" : "No"}
                </TableCell>
              </TableRow>
              {labDetail.OtherSource && (
                <>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Relevant Document
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.OtherSourcesDocument ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.OtherSourcesDocument);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Subsection: Internal Audit and Management Review */}
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
        >
          Internal Audit and Management Review
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 0 }}>
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": {
                border: "1px solid #bdbdbd",
              },
              borderCollapse: "collapse",
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Start Date of Last Internal Audit
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {formatDate(labDetail.InternalAuditFromDate)}
                </TableCell>
                {isObservationVisible && (
                  <TableCell
                    align="center"
                    sx={{ fontSize: "0.82rem" }}
                    rowSpan={7}
                    style={{ verticalAlign: "top" }}
                  >
                    {renderObservationCell(getObservationBySubSection("Internal Audit and Management Review", "internal-audit-management-review"))}
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  End Date of Last Internal Audit
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {formatDate(labDetail.InternalAuditToDate)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Internal Audit Document 
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.InternalAuditDocument ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.InternalAuditDocument);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Whether all requirements of ISO 15189:2022 covering all activities of laboratory have been audited at least once in last one year?
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.ISO15189Audited ? "Yes" : "No"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Whether various locations (including collection centers)
                  covered in the audit?
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.LocationsAudited ? "Yes" : "No"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Whether Pre and post examination activities included in the
                  audit schedule?
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.PrePostExamAudited ? "Yes" : "No"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Start Date of Last Management Review
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {formatDate(labDetail.ManagementReviewFromDate)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  End Date of Last Management Review
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {formatDate(labDetail.ManagementReviewToDate)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  Management Review Document  
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ width: "50%", fontSize: "0.82rem" }}
                >
                  {labDetail.ManagementReviewDocument ? (
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(labDetail.ManagementReviewDocument);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Section:  Lab Discipline Details */}
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
        >
          Disciplines
        </Typography>
        <TableContainer component={Paper} sx={{ mb: 3, borderRadius: 0 }}>
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": {
                border: "1px solid #bdbdbd",
              },
              borderCollapse: "collapse",
            }}
          >
            <TableHead sx={{ bgcolor: "#dbd8d8c7" }}>
              <TableRow>
                <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                  <strong>Sr No.</strong>
                </TableCell>
                <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                  <strong>Discipline Name</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {disciplineState.length > 0 ? (
                disciplineState.map((discipline, index) => (
                  <TableRow key={discipline.Id}>
                    <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                      {index + 1}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                      {discipline.DisciplineName}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3}>No disciplines specified</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Section: Scope of Testing */}
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
        >
          Proposed Scope
        </Typography>
        <TableContainer
          component={Paper}
          sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto" }}
        >
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": { border: "1px solid #bdbdbd" },
              borderCollapse: "collapse",
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                <TableCell align="right" sx={{ width: "50%" }}>
                  <strong>Details</strong>
                </TableCell>
                <TableCell align="left" sx={{ width: "50%" }}>
                  <strong>Data Feeded by Lab</strong>
                </TableCell>
                {isObservationVisible && (
                  <TableCell align="center" sx={{ width: "30%" }}>
                    <strong>Observation</strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {scopeState.length > 0 ? (
                scopeState.map((scope, idx) => {
                  // Calculate dynamic rowspan: number of fields displayed for this scope
                  const scopeFields = [
                    'Discipline',
                    'Product/Material of test',
                    'Specific Test Performed',
                    'Test method',
                    'Range Type',
                    'Range of Testing/Limit of detection',
                    '(Â±)MU @Value',
                    'Test Performed At',
                    'Test code',
                    'Range Remarks',
                    'CV Value',
                    'PT Availability',
                    'Equipment',
                    'Reference Material',
                    'Proficiency Testing'
                  ];
                  const scopeRowCount = scopeFields.length;
                  
                  return (
                  <>
                    {/* <TableRow key={scope.Id + "-location"}>
            <TableCell align="right" sx={{ fontSize: "0.82rem" }}>Location</TableCell>
            <TableCell align="left" sx={{ fontSize: "0.82rem" }}>{scope.Location || "-"}</TableCell>
          </TableRow> */}
                    <TableRow key={scope.Id + "-discipline"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Discipline
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {scope.DisciplineName || "-"}
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell
                          align="center"
                          sx={{ fontSize: "0.82rem" }}
                          rowSpan={scopeRowCount}
                          style={{ verticalAlign: "top" }}
                        >
                          {renderObservationCell(getObservationBySection("Scope", scope.Id))}
                        </TableCell>
                      )}
                    </TableRow>
                    <TableRow key={scope.Id + "-test"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Product/Material of test
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {scope.GroupTested || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={scope.Id + "-specificTest"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Specific Test Performed
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {scope.TypeOfTest || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={scope.Id + "-standard"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Test method
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {scope.ScopeStandard || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={scope.Id + "-rangeType"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Range Type
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {scope.rangeType || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={scope.Id + "-range"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Range of Testing/Limit of detection
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {`${scope.RangeLL ? scope.RangeLL : "-"} ${
                          scope.RangeLLUnit ? scope.RangeLLUnit : "-"
                        } To ${scope.RangeUL ? scope.RangeUL : "-"} ${
                          scope.RangeULUnit ? scope.RangeULUnit : "-"
                        }`}
                      </TableCell>
                    </TableRow>
                    <TableRow key={scope.Id + "-value"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        (Â±)MU @Value
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {scope.mu || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={scope.Id + "-performed"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Test Performed At
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {scope.testPerformedAt || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={scope.Id + "-Test-code"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Test code
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {scope.testCode || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={scope.Id + "-range-remarks"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Range Remarks
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {scope.rangeRemark || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={scope.Id + "-cv-value"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        CV Value
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {scope.cv || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={scope.Id + "-pt-availability"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        PT Availability
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {scope.PTAvailable || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={scope.Id + "-equipments"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Equipment
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {scope.EquipmentName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={scope.Id + "-reference-material"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Reference Material
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {scope.Ref_Id || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={scope.Id + "-Proficiency-Testing"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Proficiency Testing
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {scope.Pt_id || "-"}
                      </TableCell>
                    </TableRow>
                    {idx !== scopeState.length - 1 && (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{ bgcolor: "#f5f5f5", height: 8, border: "none" }}
                        />
                      </TableRow>
                    )}
                  </>
                );
                })
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={2}>
                    No scope details specified
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Section: Organization Staff Details */}
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
        >
          Organization Details
        </Typography>
        <TableContainer
          component={Paper}
          sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto" }}
        >
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": { border: "1px solid #bdbdbd" },
              borderCollapse: "collapse",
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                <TableCell align="right" sx={{ width: "50%" }}>
                  <strong>Details</strong>
                </TableCell>
                <TableCell align="left" sx={{ width: "50%" }}>
                  <strong>Data Feeded by Lab</strong>
                </TableCell>
                {isObservationVisible && (
                  <TableCell align="center" sx={{ width: "30%" }}>
                    <strong>Observation</strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {organisationDetailsState.length > 0 ? (
                organisationDetailsState.map((staff, idx) => {
                  // Calculate dynamic rowspan: number of fields displayed for this staff
                  const staffFields = [
                    'Discipline',
                    'Employee Type',
                    'Name',
                    'Designation',
                    'Qualification',
                    'Specialization',
                    'Experience in Years Related to Present Work',
                    'Relevant Technical Training',
                    'Landline No.',
                    'Mobile No.',
                    'Email',
                    'Engagement Type',
                    'Timing',
                    'Authorized Signatory'
                  ];
                  // Add one more if authorized signatory file is present
                  if (staff.IsAuthorisedSignatory) {
                    staffFields.push('Authorized Signatory File');
                  }
                  const staffRowCount = staffFields.length;
                  
                  return (
                  <>
                    <TableRow key={staff.Id + "-discipline"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Discipline
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {staff.DisciplineName || "-"}
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell
                          align="center"
                          sx={{ fontSize: "0.82rem" }}
                          rowSpan={staffRowCount}
                          style={{ verticalAlign: "top" }}
                        >
                          {renderObservationCell(getObservationBySection("Staff", staff.Id))}
                        </TableCell>
                      )}
                    </TableRow>
                    <TableRow key={staff.Id + "-type"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Employee Type
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {staff.EmployeeType || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={staff.Id + "-name"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Name
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {staff.Name || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={staff.Id + "-designation"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Designation
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {staff.Designation || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={staff.Id + "-qualification"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Qualification
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {staff.Qualification || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={staff.Id + "-specialization"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Specialization
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {staff.Specialization || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={staff.Id + "-experience"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Experience in Years Related to Present Work
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {staff.RelevantExperience || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={staff.Id + "-relevantTraining"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Relevant Technical Training
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {staff.RelevantTraining || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={staff.Id + "-landline"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Landline No.
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {staff.LandlineNo || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={staff.Id + "-contact"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Mobile No.
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {staff.ContactNo || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={staff.Id + "-email"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Email
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {staff.EmailID || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={staff.Id + "-engagement"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Engagement Type
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {staff.typeofEngagement || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={staff.Id + "-timing"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Timing
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {staff.Timming || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={staff.Id + "-signatory"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Authorized Signatory
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {staff.IsAuthorisedSignatory ? "Yes" : "No"}
                      </TableCell>
                    </TableRow>
                    {staff.IsAuthorisedSignatory && (
                      <TableRow key={staff.Id + "-signatoryfile"}>
                        <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                          Authorized Signatory File
                        </TableCell>
                        <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                          {staff.SignatorySign ? (
                            <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        viewS3File(staff.SignatorySign);
                      }}
                      style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    >
                      View
                    </a>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                    {idx !== organisationDetailsState.length - 1 && (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{ bgcolor: "#f5f5f5", height: 8, border: "none" }}
                        />
                      </TableRow>
                    )}
                  </>
                );
                })
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={2}>
                    No staff details specified
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Section: Equipment Details */}
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
        >
          Equipment Details
        </Typography>
        <TableContainer
          component={Paper}
          sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto" }}
        >
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": { border: "1px solid #bdbdbd" },
              borderCollapse: "collapse",
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                <TableCell align="right" sx={{ width: "50%" }}>
                  <strong>Details</strong>
                </TableCell>
                <TableCell align="left" sx={{ width: "50%" }}>
                  <strong>Data Feeded by Lab</strong>
                </TableCell>
                {isObservationVisible && (
                  <TableCell align="center" sx={{ width: "30%" }}>
                    <strong>Observation</strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {equipmentDetailsState.length > 0 ? (
                equipmentDetailsState.map((equipment, idx) => {
                  // Calculate dynamic rowspan: number of fields displayed for this equipment
                  const equipmentFields = [
                    'Discipline',
                    'Equipment Name',
                    'Equipment ID',
                    'UID',
                    'Model',
                    'Serial No',
                    'Year of Make',
                    'Receipt Date & Date Placed in Service',
                    'Calibration Date',
                    'Calibration Due on',
                    'Calibrated By',
                    'Name of Calibration Agency/External Laboratory',
                    'Date of Installation',
                    'In-house/Traceability',
                    'Name of Manufacturer',
                    'Limit of Detection'
                  ];
                  const equipmentRowCount = equipmentFields.length;
                  
                  return (
                  <>
                    <TableRow key={equipment.Id + "-discipline"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Discipline
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {equipment.DisciplineName || "-"}
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell
                          align="center"
                          sx={{ fontSize: "0.82rem" }}
                          rowSpan={equipmentRowCount}
                          style={{ verticalAlign: "top" }}
                        >
                          {renderObservationCell(getObservationBySection("Equipment", equipment.Id))}
                        </TableCell>
                      )}
                    </TableRow>
                    <TableRow key={equipment.Id + "-name"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Equipment Name
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {equipment.NameOfEquipment || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={equipment.Id + "-id"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Equipment ID
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {equipment.Equipmentid || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={equipment.Id + "-UID"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        UID
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {equipment.Uid || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={equipment.Id + "-model"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Model
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {equipment.Model || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={equipment.Id + "-Serial No"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Serial No
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {equipment.SerialNo || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={equipment.Id + "-year"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Year of Make
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {equipment.YearOfMake || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={equipment.Id + "-receipt"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Receipt Date & Date Placed in Service
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {formatDate(equipment.ReceiptDate) || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={equipment.Id + "-calibration-date"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Calibration Date
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {formatDate(equipment.DateOfLastCalibration) || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={equipment.Id + "-calibration-due-on"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Calibration Due on
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {formatDate(equipment.CalibrationDueOn) || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={equipment.Id + "-calibrated-by"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Calibrated By
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {equipment.CalibratedByType || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={equipment.Id + "-calibration-agency"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Name of Calibration Agency/External Laboratory
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {equipment.CalibratedByType === "In-house"
                          ? equipment.InHouseCalibration || "-"
                          : equipment.ExternalCalibrationAgency || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={equipment.Id + "-installation-date"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Date of Installation
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {formatDate(equipment.DateOfInstallation) || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={equipment.Id + "-In-house/Traceability"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        In-house/Traceability 
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {equipment.Traceability || equipment.InHouseTraceability || equipment.InHouseCalibration || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={equipment.Id + "-Name-of-Manufacturer"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Name of Manufacturer
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {equipment.MakeCompany || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={equipment.Id + "-Limit-of-Detection"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Limit of Detection
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {equipment.LimitOfDetection || "-"}
                      </TableCell>
                    </TableRow>
                    {idx !== equipmentDetailsState.length - 1 && (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{ bgcolor: "#f5f5f5", height: 8, border: "none" }}
                        />
                      </TableRow>
                    )}
                  </>
                );
                })
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={2}>
                    No equipment details specified
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Section: Reference Materials */}
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
        >
          Reference Material Details
        </Typography>
        <TableContainer
          component={Paper}
          sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto" }}
        >
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": { border: "1px solid #bdbdbd" },
              borderCollapse: "collapse",
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                <TableCell align="right" sx={{ width: "50%" }}>
                  <strong>Details</strong>
                </TableCell>
                <TableCell align="left" sx={{ width: "50%" }}>
                  <strong>Data Feeded by Lab</strong>
                </TableCell>
                {isObservationVisible && (
                  <TableCell align="center" sx={{ width: "30%" }}>
                    <strong>Observation</strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {referenceMaterialsState.length > 0 ? (
                referenceMaterialsState.map((material, idx) => {
                  // Calculate dynamic rowspan: number of fields displayed for this material
                  const materialFields = [
                    'Discipline',
                    'Name of Reference Material/Strain/Culture',
                    'Source',
                    'Date of Expiry',
                    'Traceability'
                  ];
                  const materialRowCount = materialFields.length;
                  
                  return (
                  <>
                    <TableRow key={material.Id + "-discipline"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Discipline
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {material.DisciplineName || "-"}
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell
                          align="center"
                          sx={{ fontSize: "0.82rem" }}
                          rowSpan={materialRowCount}
                          style={{ verticalAlign: "top" }}
                        >
                          {renderObservationCell(getObservationBySection("Reference Material", material.Id))}
                        </TableCell>
                      )}
                    </TableRow>
                    <TableRow key={material.Id + "-name"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Name of Reference Material/Strain/Culture
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {material.Name || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={material.Id + "-source"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Source
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {material.Source || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={material.Id + "-expiry"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Date of Expiry
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {formatDate(material.DateOfExpiry) === "01/01/1900" ? "-" : formatDate(material.DateOfExpiry)}
                      </TableCell>
                    </TableRow>
                    <TableRow key={material.Id + "-trace"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Traceability
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {material.Traceability || "-"}
                      </TableCell>
                    </TableRow>
                    {idx !== referenceMaterialsState.length - 1 && (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{ bgcolor: "#f5f5f5", height: 8, border: "none" }}
                        />
                      </TableRow>
                    )}
                  </>
                );
                })
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={2}>
                    No reference materials specified
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Section: Non Analytical Instruments Details */}
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
        >
          Non Analytical Instruments Details
        </Typography>
        <TableContainer
          component={Paper}
          sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto" }}
        >
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": { border: "1px solid #bdbdbd" },
              borderCollapse: "collapse",
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                <TableCell align="right" sx={{ width: "50%" }}>
                  <strong>Details</strong>
                </TableCell>
                <TableCell align="left" sx={{ width: "50%" }}>
                  <strong>Data Feeded by Lab</strong>
                </TableCell>
                {isObservationVisible && (
                  <TableCell align="center" sx={{ width: "30%" }}>
                    <strong>Observation</strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {nonAnalyticalInstrumentsState.length > 0 ? (
                nonAnalyticalInstrumentsState.map((instrument, idx) => {
                  // Calculate dynamic rowspan: number of fields displayed for this instrument
                  const instrumentFields = [
                    'InstrumentName',
                    'UID', 
                    'Calibration_Frequency',
                    'LastCalibration_Date',
                    'NextCalibration_Date'
                  ];
                  const instrumentRowCount = instrumentFields.length;
                  
                  return (
                  <>
                    <TableRow key={instrument.Id + "-InstrumentName"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Instrument Name
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {instrument.InstrumentName ? String(instrument.InstrumentName).replace(/^[,\s]+|[,\s]+$/g, '').trim() : "-"}
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell
                          rowSpan={instrumentRowCount}
                          align="center"
                          sx={{ fontSize: "0.82rem" }}
                          style={{ verticalAlign: "center" }}
                        >
                          {renderObservationCell(getObservationBySection("Non-Analytical Instruments", instrument.Id))}
                        </TableCell>
                      )}
                    </TableRow>
                    <TableRow key={instrument.Id + "-UID"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        UID
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {instrument.UID || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={instrument.Id + "-Calibration_Frequency"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Calibration Frequency
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {instrument.Calibration_Frequency || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={instrument.Id + "-LastCalibration_Date"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Last Calibration Date
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {formatDate(instrument.LastCalibration_Date) || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={instrument.Id + "-NextCalibration_Date"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Next Calibration Date
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {formatDate(instrument.NextCalibration_Date) || "-"}
                      </TableCell>
                    </TableRow>      
                    {idx !== nonAnalyticalInstrumentsState.length - 1 && (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{ bgcolor: "#f5f5f5", height: 8, border: "none" }}
                        />
                      </TableRow>
                    )}
                  </>
                );
                })
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={2}>
                    No Non Analytical Instruments Specified
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Section: Phantoms Details */}
        {(labDetail.CabType === 'Medical Imaging' || labDetail.CabType === 'Both') && (
          <>
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
        >
          Phantoms Details
        </Typography>
        <TableContainer
          component={Paper}
          sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto" }}
        >
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": { border: "1px solid #bdbdbd" },
              borderCollapse: "collapse",
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                <TableCell align="right" sx={{ width: "50%" }}>
                  <strong>Details</strong>
                </TableCell>
                <TableCell align="left" sx={{ width: "50%" }}>
                  <strong>Data Feeded by Lab</strong>
                </TableCell>
                {isObservationVisible && (
                  <TableCell align="center" sx={{ width: "30%" }}>
                    <strong>Observation</strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {form153PhantomsState.length > 0 ? (
                form153PhantomsState.map((phantom, idx) => (
                  <>
                    <TableRow key={phantom.Id + "-PhantomName"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Phantom Type
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {phantom.PhantomType || "-"}
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell
                          align="center"
                          sx={{ fontSize: "0.82rem" }}
                          rowSpan={nonAnalyticalInstrumentsState.length}
                          style={{ verticalAlign: "top" }}
                        >
                          {renderObservationCell(getObservationBySection("phantoms", phantom.Id))}
                        </TableCell>
                      )}
                    </TableRow>
                    <TableRow key={phantom.Id + "-Discipline"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Discipline
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {phantom.DisciplineName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={phantom.Id + "-typeoftest"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Type of Test
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {phantom.TypeOfTestNames || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={phantom.Id + "-Specifications"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Specifications
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {phantom.Specifications || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={phantom.Id + "-DateofPlacing"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Date of Placing
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {formatDate(phantom.DateofPlacing) || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={phantom.Id + "-Traceability"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Traceability
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {phantom.Traceability || "-"}
                      </TableCell>
                    </TableRow>      
                    {idx !== form153PhantomsState.length - 1 && (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{ bgcolor: "#f5f5f5", height: 8, border: "none" }}
                        />
                      </TableRow>
                    )}
                  </>
                ))
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={2}>
                    No Phantoms Specified
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </>
        )}
        {/* Section: Radioisotopes/Test Kits Details */}
        {(labDetail.CabType === 'Medical Imaging' || labDetail.CabType === 'Both') && (
          <>
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
        >
          Radioisotopes/Test Kits Details
        </Typography>
        <TableContainer
          component={Paper}
          sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto" }}
        >
          <Table
            size="small"
            sx={{
              border: "1px solid #bdbdbd",
              "& th, & td": { border: "1px solid #bdbdbd" },
              borderCollapse: "collapse",
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                <TableCell align="right" sx={{ width: "50%" }}>
                  <strong>Details</strong>
                </TableCell>
                <TableCell align="left" sx={{ width: "50%" }}>
                  <strong>Data Feeded by Lab</strong>
                </TableCell>
                {isObservationVisible && (
                  <TableCell align="center" sx={{ width: "30%" }}>
                    <strong>Observation</strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {radioDetailsState.length > 0 ? (
                radioDetailsState.map((radio, idx) => (
                  <>
                    <TableRow key={radio.Id + "-RadioName"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Name of Radioisotopes
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {radio.NameofRadioisotopes || "-"}
                      </TableCell>
                      {isObservationVisible && (
                        <TableCell
                          align="center"
                          sx={{ fontSize: "0.82rem" }}
                          rowSpan={nonAnalyticalInstrumentsState.length}
                          style={{ verticalAlign: "top" }}
                        >
                          {renderObservationCell(getObservationBySection("radioisotopes", radio.Id))}
                        </TableCell>
                      )}
                    </TableRow>
                    <TableRow key={radio.Id + "-Discipline"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Discipline
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {radio.DisciplineName || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={radio.Id + "-typeoftest"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Type of Test
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {radio.TypeOfTestNames || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={radio.Id + "-HalfLife"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Half Life
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {radio.HalfLife || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={radio.Id + "-Energy"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Energy (KeV)
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {radio.EnergyKeV || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={radio.Id + "-MaxActivityPermitted"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Max Activity Permitted
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {radio.MaxactivityPermitted || "-"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={radio.Id + "-DetailsOfSupplier"}>
                      <TableCell align="right" sx={{ fontSize: "0.82rem" }}>
                        Details of Supplier
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem" }}>
                        {radio.DetailsofSupplier || "-"}
                      </TableCell>
                    </TableRow>          
                    {idx !== radioDetailsState.length - 1 && (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          sx={{ bgcolor: "#f5f5f5", height: 8, border: "none" }}
                        />
                      </TableRow>
                    )}
                  </>
                ))
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={2}>
                    No Radioisotopes/Test Kits Specified
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </>
        )}
</Box>
        {/* Audit Trail Section */}
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1, mt: 3 }}
        >
          Audit Trail Logs
        </Typography>

        {loadingAuditTrail ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading audit trail logs...</Typography>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ mb: 3, borderRadius: 0, maxWidth: "100%", mx: "auto" }}
          >
            <Table
              size="small"
              sx={{
                border: "1px solid #bdbdbd",
                "& th, & td": { border: "1px solid #bdbdbd" },
                borderCollapse: "collapse",
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                  <TableCell align="center" sx={{ width: "30%" }}>
                    <strong>Section & Observation</strong>
                  </TableCell>
                  <TableCell align="center" sx={{ width: "50%" }}>
                    <strong>Multiple Observations</strong>
                  </TableCell>
                  <TableCell align="center" sx={{ width: "20%" }}>
                    <strong>Date & Time History</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const filteredData = getFilteredAuditTrailData();
                  return filteredData.length > 0 ? (
                    filteredData.map((item, index) => (
                    <TableRow 
                      key={`${item.id}-${index}`}
                      sx={{
                        backgroundColor: "#f8f9fa",
                        "&:hover": { backgroundColor: "#e3f2fd" }
                      }}
                    >
                      <TableCell align="left" sx={{ fontSize: "0.82rem", fontWeight: "bold" }}>
                        {item.sectionName || "-"}
                        <Typography variant="caption" sx={{ 
                          display: "block", 
                          color: "#1976d2", 
                          fontSize: "0.75rem",
                          fontWeight: "bold"
                        }}>
                          {item.observationLabel}
                        </Typography>
                        {/* Display the appropriate name based on section type */}
                        {item.scopeName && (
                          <Typography variant="caption" sx={{ 
                            display: "block", 
                            color: "#666", 
                            fontSize: "0.72rem",
                            fontWeight: "normal",
                            mt: 0.5
                          }}>
                            {item.scopeName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: "0.82rem", maxWidth: "350px" }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {item.allObservations.map((observation, idx) => (
                            <Typography key={idx} variant="body2" sx={{ 
                              fontSize: "0.82rem",
                              padding: '4px 8px', 
                              backgroundColor: idx % 2 === 0 ? '#f0f8ff' : '#f5f5f5', 
                              borderRadius: '4px',
                              border: '1px solid #e0e0e0'
                            }}>
                              <strong>Obs {idx + 1}:</strong> {observation}
                            </Typography>
                          ))}
                          {item.allObservations.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.82rem" }}>
                              No observations recorded
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: "0.82rem" }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {item.allDates.map((date, idx) => (
                            <Typography key={idx} variant="body2" sx={{ fontSize: "0.75rem" }}>
                              {formatDateTime(date)}
                            </Typography>
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={5} sx={{ p: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        {selectedSection === 'all' 
                          ? "No audit trail logs found for this application"
                          : `No audit trail logs found for section "${selectedSection}"`
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>
    </Box>
  );
};

export default AuditTrail;
